import { graphql } from "@opensea/graphql"
import { useCallback } from "react"
import { useClient as useUrqlClient } from "urql"
import { useSnapshot } from "valtio"
import { toLowerCase } from "../lib/string"
import { useAddress } from "../polychain/hooks/useAddress"
import { PolychainError } from "../polychain/lib/errors"
import { BlurActions, BlurState, isValidToken } from "../stores/blurStore"
import type { Address } from "../types"

const GetBlurAuthTokenQuery = graphql(
  `
    query GetBlurAuthToken(
      $address: String!
      $expiresOn: String!
      $hmac: String!
      $message: String!
      $signature: String!
      $chain: ChainIdentifier!
    ) {
      blurAuthToken(
        address: $address
        expiresOn: $expiresOn
        hmac: $hmac
        message: $message
        signature: $signature
        chain: $chain
      ) {
        accessToken
      }
    }
  `,
  [],
)

type GetBlurAuthTokenArgs = {
  address: Address
  expiresOn: string
  hmac: string
  message: string
  signature: string
  chain: string
}

export function useBlurAuthToken() {
  const currentAddress = useAddress()
  const { tokens } = useSnapshot(BlurState)
  const token = currentAddress ? tokens[currentAddress] : null
  return token && isValidToken(token) ? token.accessToken : null
}

export const useBlurAuth = () => {
  const urqlClient = useUrqlClient()
  const currentAddress = useAddress()
  const { tokens } = useSnapshot(BlurState)
  const isAuthenticated = currentAddress
    ? isValidToken(tokens[currentAddress])
    : false

  const blurAuth = useCallback(
    async (args: GetBlurAuthTokenArgs) => {
      const { data } = await urqlClient.query(GetBlurAuthTokenQuery, {
        ...args,
        address: toLowerCase(args.address),
      })
      const accessToken = data?.blurAuthToken.accessToken

      if (accessToken) {
        BlurActions.setToken(args.address, {
          accessToken,
          expiresOn: args.expiresOn,
        })
      } else {
        throw new PolychainError("Blur Auth failed", "BlurAuthError")
      }
    },
    [urqlClient],
  )

  return {
    isAuthenticated,
    blurAuth,
    clear: BlurActions.clear,
  }
}
